/**
 * TrajectoryRead — the dossier's editorial TRAJECTORY read (2026-06-13).
 *
 * Replaces the autonomous bullish/bearish sentiment chip in the Brief's LATEST
 * ASSESSMENT header with an evidence-derived trajectory label (Building / Steady /
 * Cooling / Dormant / Too-early). The label is set upstream by rules + hysteresis
 * (entity_trajectory/{tagId}); this component is purely presentational.
 *
 * GUARDRAIL — label never naked: when `reasons` is empty we render "limited signal"
 * instead of a bare label, so we never assert a trajectory with no receipts behind
 * it. Confidence is stated honestly ("low confidence · single source").
 *
 * DEGRADATION: the entity_trajectory collection is NOT seeded in prod yet, so the
 * common case is a MISSING doc → the caller passes `trajectory == null` and renders
 * nothing in this slot (this component is only mounted when a doc exists). When the
 * state is unknown/unmapped we render nothing rather than a broken chip.
 *
 * Server-component-safe — no 'use client', no interactivity.
 */

export type TrajectoryState =
  | 'building'
  | 'steady'
  | 'cooling'
  | 'dormant'
  | 'too_early';

export interface EntityTrajectory {
  state: TrajectoryState;
  reasons: string[];
  confidence: 'low' | 'medium';
  updatedAt?: string;
}

/**
 * State → { glyph, label, colorToken } using existing dossier tokens.
 * Exported as the ONE canonical trajectory label map — reused by the
 * programmatic article trajectory stub (EntityTrajectoryStub) so the
 * dossier and the article chip never drift on glyph/label/color.
 */
export const STATE_META: Record<
  TrajectoryState,
  { glyph: string; label: string; color: string }
> = {
  building: { glyph: '▲', label: 'Building', color: 'text-signal-green-text' },
  steady: { glyph: '●', label: 'Steady', color: 'text-parchment' },
  cooling: { glyph: '▼', label: 'Cooling', color: 'text-scout-gold' },
  dormant: { glyph: '○', label: 'Dormant', color: 'text-parchment-dim' },
  too_early: { glyph: '◌', label: 'Too early', color: 'text-parchment-dim' },
};

/** Short "as of Jun 13" for the trajectory's updatedAt. Null when invalid/absent. */
function asOfShort(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function TrajectoryRead({
  state,
  reasons,
  confidence,
  updatedAt,
}: EntityTrajectory) {
  const meta = STATE_META[state];
  // Unknown/unmapped state → render nothing (never a broken chip).
  if (!meta) return null;

  const cleanReasons = (reasons ?? [])
    .filter((r): r is string => typeof r === 'string' && r.trim() !== '')
    .slice(0, 3);
  const hasReasons = cleanReasons.length > 0;

  const confidenceLine =
    confidence === 'medium'
      ? 'medium confidence'
      : 'low confidence · single source';

  const asOf = asOfShort(updatedAt);

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-mono text-[10px]">
      {/* Glyph + label — the trajectory read. */}
      <span className={`font-semibold ${meta.color}`}>
        {meta.glyph} {meta.label}
      </span>

      {/* Receipts — cited reasons, or the never-naked "limited signal" fallback. */}
      {hasReasons ? (
        cleanReasons.map((r, i) => (
          <span key={i} className="text-parchment-dim">
            · {r}
          </span>
        ))
      ) : (
        <span className="text-parchment-dim">· limited signal</span>
      )}

      {/* Honest confidence. */}
      <span className="text-parchment-dim">· {confidenceLine}</span>

      {/* Optional as-of date. */}
      {asOf && <span className="text-parchment-dim/70">· as of {asOf}</span>}
    </span>
  );
}
